import { useState } from 'react';
import type { TRowSelection } from './useRowSelection.types.ts';

const createEmptySelection = (): ReadonlySet<string> => new Set();

export const useRowSelection = (
  visibleIds: readonly string[],
): TRowSelection => {
  const [selectedIds, setSelectedIds] = useState(createEmptySelection);
  const visibleSelectedIds = visibleIds.filter((id) => selectedIds.has(id));

  if (visibleSelectedIds.length < selectedIds.size) {
    setSelectedIds(new Set(visibleSelectedIds));
  }

  const hasSelection = visibleSelectedIds.length > 0;
  const isPartlySelected =
    hasSelection && visibleSelectedIds.length < visibleIds.length;

  const isRowSelected = (id: string): boolean => selectedIds.has(id);

  const setRowSelected = (id: string, isSelected: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (isSelected) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  };

  const clearSelection = () => setSelectedIds(createEmptySelection());

  const toggleAll = () => {
    if (hasSelection) {
      clearSelection();

      return;
    }

    setSelectedIds(new Set(visibleIds));
  };

  return {
    selectedIds: visibleSelectedIds,
    hasSelection,
    isPartlySelected,
    isRowSelected,
    setRowSelected,
    toggleAll,
    clearSelection,
  };
};
